import React, { useEffect, useState, useMemo } from 'react';
import { dailyPlansAPI } from '../services/api';
import '../styles/myLogs.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';

/* ── Helpers ── */
function fmtDate(dateStr) {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('default', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDay(dateStr) {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('default', { weekday: 'short' });
}
function sumMinutes(tasks = []) {
    return tasks.reduce((s, t) => s + Math.round((parseFloat(t.actualHours) || 0) * 60), 0);
}
function fmtHrs(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0 && m === 0) return '—';
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
function toISO(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmt12(timeStr) {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${((h % 12) || 12).toString().padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
}

/* ─────────────────────────────────────────
   Main Page
───────────────────────────────────────── */
const MyLogsPage = () => {
    const navigate = useNavigate();

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedProject, setSelectedProject] = useState('all');

    // Fetch today's plan to know checkedIn / logsSubmitted / checkedOut state
    const [todayPlan, setTodayPlan] = useState(null);

    useEffect(() => { loadLogs(); loadTodayPlan(); }, []);

    const loadLogs = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await dailyPlansAPI.getMyPlans();
            setPlans(res.data);
        } catch (err) {
            console.error(err);
            setError('Failed to load your logs. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadTodayPlan = async () => {
        try {
            const res = await dailyPlansAPI.getToday();
            setTodayPlan(res.data);
        } catch (err) {
            console.error('Failed to load today plan:', err);
        }
    };

    /* ── Derived today flags ── */
    const isCheckedIn = todayPlan?.checkedIn;
    const isLogsSubmitted = todayPlan?.logsSubmitted;
    const isCheckedOut = todayPlan?.checkedOut;
    const hasAtLeastOneLog = todayPlan?.tasks?.some(t => t.notes && t.notes.trim() !== '');

    const getTotalLoggedHours = () =>
        todayPlan?.tasks?.reduce((sum, t) => sum + (t.actualHours || 0), 0) || 0;

    const handleCheckOut = async () => {
        if (!isLogsSubmitted) { alert('Please submit your logs before checking out.'); return; }
        if (isCheckedOut) { alert('You have already checked out for today.'); return; }

        const totalLogged = getTotalLoggedHours();
        const ok = window.confirm(`You have entered ${totalLogged.toFixed(2)} hrs of log.\nAre you sure you want to check out?`);
        if (!ok) return;

        try {
            await dailyPlansAPI.checkOut();
            await loadTodayPlan();
            alert('Checked out successfully! Have a great evening 👋');
        } catch (err) {
            alert(err.response?.data?.message || 'Checkout failed');
        }
    };

    /* ── Filter helpers ── */
    const months = useMemo(() => {
        const seen = new Set();
        const list = [];
        plans.forEach(p => {
            const d = new Date(p.date);
            if (isNaN(d)) return;
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (!seen.has(key)) {
                seen.add(key);
                list.push({ key, label: d.toLocaleString('default', { month: 'long', year: 'numeric' }) });
            }
        });
        return list.sort((a, b) => b.key.localeCompare(a.key));
    }, [plans]);

    const projects = useMemo(() => {
        const seen = new Set();
        plans.forEach(p => (p.tasks || []).forEach(t => {
            seen.add(t.backlogTask?.project?.name || 'Free Time');
        }));
        return [...seen].sort();
    }, [plans]);

    const rows = useMemo(() => {
        const result = [];
        const today = new Date();
        plans.forEach(p => {
            const d = new Date(p.date);
            if (selectedMonth === 'all') {
                const isToday =
                    d.getDate() === today.getDate() &&
                    d.getMonth() === today.getMonth() &&
                    d.getFullYear() === today.getFullYear();
                if (!isToday) return;
            } else {
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (key !== selectedMonth) return;
            }
            (p.tasks || []).forEach(t => {
                const project = t.backlogTask?.project?.name || 'Free Time';
                if (selectedProject !== 'all' && project !== selectedProject) return;
                result.push({
                    id: t.id,
                    date: p.date,
                    dateLabel: fmtDate(p.date),
                    dayLabel: fmtDay(p.date),
                    task: t.title,
                    project,
                    notes: t.notes || '',
                    minutes: Math.round((parseFloat(t.actualHours) || 0) * 60),
                    completed: t.completed,
                });
            });
        });
        return result.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [plans, selectedMonth, selectedProject]);

    const projectTotals = useMemo(() => {
        const map = {};
        rows.forEach(r => {
            if (!map[r.project]) map[r.project] = 0;
            map[r.project] += r.minutes;
        });
        return Object.entries(map).sort((a, b) => b[1] - a[1]);
    }, [rows]);

    const grandTotal = rows.reduce((s, r) => s + r.minutes, 0);
    const hasActiveFilter = selectedMonth !== 'all' || selectedProject !== 'all';

    const handleExport = () => {
        if (!rows || rows.length === 0) { alert('No data to export'); return; }
        let totalMinutes = 0;
        const data = rows.map(r => {
            totalMinutes += r.minutes;
            return [r.dateLabel, r.task, r.project, r.notes || '', r.completed ? 'Completed' : 'Pending', fmtHrs(r.minutes)];
        });
        const header = ['Date', 'Task', 'Project', 'Logs', 'Status', 'Hours'];
        const worksheet = XLSX.utils.aoa_to_sheet([header, ...data]);
        worksheet['!cols'] = [{ wch: 15 }, { wch: 30 }, { wch: 20 }, { wch: 40 }, { wch: 12 }, { wch: 10 }];
        XLSX.utils.sheet_add_aoa(worksheet, [['', '', '', '', 'Total Hours', fmtHrs(totalMinutes)]], { origin: -1 });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'My Logs');
        const buf = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), `MyLogs-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <div className="logs-page">

            {/* ── Header ── */}
            <div className="logs-page-header">
                <div className="logs-header-top">
                    <button className="logs-back-btn" onClick={() => navigate('/')}>← Back</button>

                    <div className="logs-title-block">
                        <h1 className="logs-page-title">📋 My Logs</h1>
                        <p className="logs-page-sub">Your complete daily work history</p>
                    </div>

                    {/* Action buttons — mirrors the main dashboard header */}

                </div>

                {!loading && !error && (
                    // <div className="logs-stats-strip">
                    //     <div className="logs-stat-pill">📅 <strong>{plans.length}</strong>&nbsp;Days Logged</div>
                    //     <div className="logs-stat-pill">✅ <strong>{plans.reduce((s, p) => s + (p.tasks?.length || 0), 0)}</strong>&nbsp;Tasks</div>
                    //     <div className="logs-stat-pill">⏱ <strong>{fmtHrs(plans.reduce((s, p) => s + sumMinutes(p.tasks), 0))}</strong>&nbsp;Total Hours</div>
                    // </div>
                    <div className="logs-stats-row">

                        {/* LEFT SIDE - STATS */}
                        <div className="logs-stats-strip">
                            <div className="logs-stat-pill">
                                📅 <strong>{plans.length}</strong>&nbsp;Days Logged
                            </div>
                            <div className="logs-stat-pill">
                                ✅ <strong>{plans.reduce((s, p) => s + (p.tasks?.length || 0), 0)}</strong>&nbsp;Tasks
                            </div>
                            <div className="logs-stat-pill">
                                ⏱ <strong>{fmtHrs(plans.reduce((s, p) => s + sumMinutes(p.tasks), 0))}</strong>&nbsp;Total Hours
                            </div>
                        </div>

                        {/* RIGHT SIDE - BUTTONS */}
                        <div className="logs-stats-actions">

                            <button
                                className="logs-header-btn calendar"
                                onClick={() => navigate('/calendar')}
                            >
                                📅 Calendar
                            </button>

                            {isCheckedIn && !isCheckedOut && (
                                <button
                                    className="logs-header-btn enter-logs"
                                    onClick={() => navigate('/logs')}
                                >
                                    📝 Enter Logs
                                </button>
                            )}

                            {isLogsSubmitted && hasAtLeastOneLog && !isCheckedOut && (
                                <button
                                    className="logs-header-btn checkout"
                                    onClick={handleCheckOut}
                                >
                                    🚪 Check-Out
                                </button>
                            )}

                        </div>
                    </div>
                )}
            </div>

            {/* ── Body ── */}
            <div className="logs-body">

                {/* Filters — View Calendar button REMOVED */}
                {!loading && !error && plans.length > 0 && (
                    <div className="logs-filter-row">
                        <div className="logs-filter-group">
                            <label className="logs-filter-label" htmlFor="f-month">Month</label>
                            <div className="logs-select-wrap">
                                <select id="f-month" className="logs-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
                                    <option value="all">Today</option>
                                    {months.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                                </select>
                                <span className="logs-select-arrow">▾</span>
                            </div>
                        </div>
                        <div className="logs-filter-group">
                            <label className="logs-filter-label" htmlFor="f-project">Project</label>
                            <div className="logs-select-wrap">
                                <select id="f-project" className="logs-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                                    <option value="all">All projects</option>
                                    {projects.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                                <span className="logs-select-arrow">▾</span>
                            </div>
                        </div>
                        {hasActiveFilter && (
                            <button className="logs-clear-btn" onClick={() => { setSelectedMonth('all'); setSelectedProject('all'); }}>✕ Clear</button>
                        )}
                        {rows.length > 0 && (
                            <div className="logs-result-count">{rows.length} task{rows.length !== 1 ? 's' : ''} · {fmtHrs(grandTotal)}</div>
                        )}
                        <div className="logs-action-btns">
                            <button className="logs-export-btn" onClick={handleExport}>
                                📥 Export
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading */}
                {loading && [1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="logs-skeleton" style={{ animationDelay: `${i * 60}ms`, height: 44 }} />
                ))}

                {/* Error */}
                {!loading && error && (
                    <div className="logs-error">
                        <span className="logs-error-text">⚠️ {error}</span>
                        <button className="logs-retry-btn" onClick={loadLogs}>Retry</button>
                    </div>
                )}

                {/* Empty */}
                {!loading && !error && rows.length === 0 && (
                    <div className="logs-empty">
                        <div className="logs-empty-icon">📭</div>
                        <div className="logs-empty-title">No logs found</div>
                        <div className="logs-empty-sub">
                            {hasActiveFilter ? 'No entries match your filters.' : 'Start logging your daily tasks to see them here.'}
                        </div>
                    </div>
                )}

                {/* Table */}
                {!loading && !error && rows.length > 0 && (
                    <>
                        <div className="logs-table-wrap">
                            <table className="logs-table">
                                <thead>
                                    <tr>
                                        <th className="col-date">Date</th>
                                        <th className="col-task">Task</th>
                                        <th className="col-project">Project</th>
                                        <th className="col-notes">Logs</th>
                                        <th className="col-status">Status</th>
                                        <th className="col-hours">Logged Hours</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => {
                                        const prevDate = i > 0 ? rows[i - 1].date : null;
                                        const isNewDate = r.date !== prevDate;
                                        return (
                                            <tr key={r.id} className={`log-row${isNewDate && i > 0 ? ' date-sep' : ''}`}>
                                                <td className="col-date">
                                                    {isNewDate ? (
                                                        <div className="log-date-cell">
                                                            <span className="log-date-day">{r.dayLabel}</span>
                                                            <span className="log-date-full">{r.dateLabel}</span>
                                                        </div>
                                                    ) : <span className="log-date-blank" />}
                                                </td>
                                                <td className="col-task"><span className="log-task-name">{r.task}</span></td>
                                                <td className="col-project"><span className="log-project-badge">{r.project}</span></td>
                                                <td className="col-notes">
                                                    {r.notes ? <span className="log-notes-text">{r.notes}</span> : <span className="log-notes-empty">—</span>}
                                                </td>
                                                <td className="col-status">
                                                    <span className={`log-status-dot ${r.completed ? 'Completed' : 'pending'}`}>
                                                        {r.completed ? 'Completed' : 'Pending'}
                                                    </span>
                                                </td>
                                                <td className="col-hours">
                                                    <span className={`log-hrs ${r.minutes > 0 ? 'has' : 'nil'}`}>{fmtHrs(r.minutes)}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Project totals */}
                        <div className="logs-totals">
                            <div className="logs-totals-heading">Hours by project</div>
                            <div className="logs-totals-rows">
                                {projectTotals.map(([name, mins]) => (
                                    <div key={name} className="logs-totals-row">
                                        <span className="logs-totals-project">{name}</span>
                                        <div className="logs-totals-bar-wrap">
                                            <div className="logs-totals-bar" style={{ width: `${Math.round((mins / grandTotal) * 100)}%` }} />
                                        </div>
                                        <span className="logs-totals-hrs">{fmtHrs(mins)}</span>
                                    </div>
                                ))}
                                <div className="logs-totals-grand">
                                    <span>Total</span>
                                    <span className="logs-totals-grand-val">{fmtHrs(grandTotal)}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyLogsPage;